<template>
  <m-content
    v-if="show"
    padding-xs
    class="changelog-card"
  >
    <m-card
      :level="1"
      no-padding
      class="_card"
    >
      <button
        type="button"
        class="_open"
        @click="open"
      >
        <span class="_label">
          {{ $t('helpButton.whatsNew') }}
        </span>
        <span class="_title">
          {{ latestReleaseNote.title }}
        </span>
      </button>
      <m-btn
        class="_close"
        xs
        fab
        light
        icon="close"
        hide-border
        :aria-label="$t('general.close')"
        @click="dismiss"
      />
    </m-card>
  </m-content>
</template>

<script setup>
import useLocalStorage from '@/composables/local-storage/local-storage';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import useReleaseNotes from '@/composables/release-notes/release-notes';
import { EventBus } from '@/lib/event-bus';
import { computed } from 'vue';

const { loggedInUser } = useLoggedInUser();
const personalAppSettingsSvc = usePersonalAppSettings(loggedInUser);
const { latestReleaseNote, unreadReleaseNotes } = useReleaseNotes(personalAppSettingsSvc, loggedInUser);
const { isExpired } = useLoggedInUserAccount();

const { data: dismissedId } = useLocalStorage(
  localStorage,
  { getKey: () => computed(() => `${loggedInUser.value?.uid}_changelog_card_dismissed`) },
  '',
);

const show = computed(() => {
  if (isExpired.value || latestReleaseNote.value === null) {
    return false;
  }
  if (dismissedId.value === latestReleaseNote.value._id) {
    return false;
  }
  return unreadReleaseNotes.value.some((next) => next._id === latestReleaseNote.value._id);
});

const dismiss = () => {
  dismissedId.value = latestReleaseNote.value._id;
};

const open = () => {
  dismiss();
  EventBus.$emit('show-release-notes');
};
</script>

<style scoped lang="scss" type="text/scss">
  .changelog-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;

    ._open {
      display: flex;
      flex-direction: column;
      gap: .6rem;
      width: 100%;
      padding: $small-container-padding-y 3.6rem $small-container-padding-y $small-container-padding-y;
      font-size: $font-size-3;
      line-height: 1.6rem;
      text-align: left;
      cursor: pointer;
      background: none;
      border: none;
      border-radius: $default-border-radius;
    }

    ._label {
      color: $font-color-secondary;
    }

    ._title {
      font-weight: $font-weight-medium;
      color: $font-color-primary;
    }

    ._close {
      position: absolute;
      top: .6rem;
      right: .6rem;
      opacity: 0;
      transition: opacity .15s ease-in-out;
    }

    ._card:hover,
    ._card:focus-within {
      ._close {
        opacity: 1;
      }
    }

    @media (hover: none) {
      ._close {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      ._close {
        transition: none;
      }
    }
  }
</style>
